// ============================================
// I18N STORE - Internationalization utilities
// ============================================

import Alpine from 'alpinejs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import localeData from 'dayjs/plugin/localeData';

// Locales de Day.js
import 'dayjs/locale/es';
import 'dayjs/locale/en';

// i18n-iso-countries for country names
import countries from 'i18n-iso-countries';
import esCountries from 'i18n-iso-countries/langs/es.json';
import enCountries from 'i18n-iso-countries/langs/en.json';

// Register country locales
countries.registerLocale(esCountries);
countries.registerLocale(enCountries);

// Import translations
import { t } from '../i18n';

// WhatsApp countries (dial codes + localized names)
import { getWhatsappCountries, type WhatsappCountry } from '../data/whatsapp-countries';

// Configurar plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(localeData);

interface SupportedLanguage {
    key: string;
    label: string;
}

interface I18nStore {
    currentLang: string;
    supportedLanguages: SupportedLanguage[];
    detectLanguage(): string;
    formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string;
    formatDateTime(date: string | Date, options?: Intl.DateTimeFormatOptions): string;
    formatRelativeDate(date: string | Date): string;
    getMonths(): Array<{ value: number; label: string }>;
    now(): String;
    dayjs: typeof dayjs;
    t(key: string, fallback?: string): string;
    getCountriesForSelect2(includeEmpty?: boolean, emptyText?: string): Array<{ id: string; text: string }>;
    getCountryName(countryCode: string): string;
    getWhatsappCountries(): WhatsappCountry[];
}

const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
    { key: 'es', label: 'Español' },
    { key: 'en', label: 'English' },
    // { key: 'fr', label: 'Français' },
    // { key: 'pt', label: 'Português' },
    // { key: 'de', label: 'Deutsch' },
    // { key: 'it', label: 'Italiano' },
];

function detectLanguageFromURL(): string {
    const path = window.location.pathname;
    const pathParts = path.split('/').filter(Boolean);

    const supportedKeys = SUPPORTED_LANGUAGES.map(lang => lang.key);

    if (pathParts.length > 0 && supportedKeys.includes(pathParts[0])) {
        return pathParts[0];
    }

    return 'en'; // Default language
}

export function registerI18nStore(): void {
    const currentLang = detectLanguageFromURL();

    // Configurar locale global de Day.js
    dayjs.locale(currentLang);

    Alpine.store('i18n', {
        currentLang,
        supportedLanguages: SUPPORTED_LANGUAGES,
        dayjs,

        detectLanguage() {
            return detectLanguageFromURL();
        },

        dateLtNow(date: string | Date): boolean {
            const now = dayjs();
            let dayjsObj: dayjs.Dayjs;

            if (date instanceof Date) {
                dayjsObj = dayjs(date);
            } else if (/[+-]\d{2}:\d{2}|Z$/i.test(date)) {
                // Ya tiene timezone explícita
                dayjsObj = dayjs(date);
            } else {
                // Asumir California timezone
                dayjsObj = dayjs.tz(date, 'America/Los_Angeles');
            }

            // Convertir a zona horaria local del navegador
            const localDate = dayjsObj.local();

            return localDate.isBefore(now);
        },

        formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions) {
            // Parsear fecha desde California
            let dayjsObj: dayjs.Dayjs;

            if (date instanceof Date) {
                dayjsObj = dayjs(date);
            } else if (/[+-]\d{2}:\d{2}|Z$/i.test(date)) {
                // Ya tiene timezone explícita
                dayjsObj = dayjs(date);
            } else {
                // Asumir California timezone
                dayjsObj = dayjs.tz(date, 'America/Los_Angeles');
            }

            // Convertir a zona horaria local del navegador
            const localDate = dayjsObj.local();

            // Formatear según locale
            if (options) {
                // Si hay opciones personalizadas, usar Intl.DateTimeFormat
                return new Intl.DateTimeFormat(this.currentLang, options).format(localDate.toDate());
            }

            // Formato por defecto: "9 de enero de 2024" (es) / "January 9, 2024" (en)
            return localDate.locale(this.currentLang).format('LL');
        },

        formatDateTime(date: string | Date, options?: Intl.DateTimeFormatOptions) {
            // Parsear fecha desde California
            let dayjsObj: dayjs.Dayjs;

            if (date instanceof Date) {
                dayjsObj = dayjs(date);
            } else if (/[+-]\d{2}:\d{2}|Z$/i.test(date)) {
                // Ya tiene timezone explícita
                dayjsObj = dayjs(date);
            } else {
                // Asumir California timezone
                dayjsObj = dayjs.tz(date, 'America/Los_Angeles');
            }

            // Convertir a zona horaria local del navegador
            const localDate = dayjsObj.local();

            // Formatear según locale
            if (options) {
                // Si hay opciones personalizadas, usar Intl.DateTimeFormat
                return new Intl.DateTimeFormat(this.currentLang, options).format(localDate.toDate());
            }

            // Formato por defecto con hora: "9 ene 2024, 14:30" (es) / "Jan 9, 2024, 2:30 PM" (en)
            return localDate.locale(this.currentLang).format('lll');
        },

        formatRelativeDate(date: string | Date): string {
            // Parsear fecha desde California
            let dayjsObj: dayjs.Dayjs;

            if (date instanceof Date) {
                dayjsObj = dayjs(date);
            } else if (/[+-]\d{2}:\d{2}|Z$/i.test(date)) {
                dayjsObj = dayjs(date);
            } else {
                dayjsObj = dayjs.tz(date, 'America/Los_Angeles');
            }

            // Convertir a zona horaria local
            const localDate = dayjsObj.local();

            // Formatear tiempo relativo según locale
            return localDate.locale(this.currentLang).fromNow();
        },

        getMonths(): Array<{ value: number; label: string }> {
            // Obtener los nombres de los meses localizados desde Day.js
            const monthsNames = dayjs().locale(this.currentLang).localeData().months();
            return monthsNames.map((label: string, index: number) => ({
                id: index + 1,
                text: label.charAt(0).toUpperCase() + label.slice(1),
                value: index + 1, // Meses de 1-12
                label: label.charAt(0).toUpperCase() + label.slice(1) // Capitalizar primera letra
            }));
        },

        now(): String {
            return dayjs().toString();
        },

        /**
         * Get translation for a key in current language
         * @param key - Translation key (supports dot notation like 'form.fail')
         * @param fallback - Optional fallback text if key not found
         * @returns Translated string
         */
        t(key: string, fallback?: string): string {
            return t(key, this.currentLang, fallback);
        },

        /**
         * Get countries list formatted for Select2 dropdown
         * Returns countries with English ID and translated text based on current language
         *
         * @param includeEmpty - Whether to include empty option at the start
         * @param emptyText - Text for empty option
         * @returns Array of {id, text} objects for Select2
         */
        getCountriesForSelect2(includeEmpty: boolean = true, emptyText?: string): Array<{ id: string; text: string }> {
            // Get country names in current language
            const countryNames = countries.getNames(this.currentLang, { select: 'official' });

            // Convert to Select2 format: id=English name, text=translated name
            const countryList = Object.entries(countryNames).map(([code, name]) => ({
                id: countries.getName(code, 'en', { select: 'official' }) || code, // ID in English
                text: name as string // Text in current language
            }))
                .filter(country => country.id) // Remove entries without English name
                .sort((a, b) => a.text.localeCompare(b.text, this.currentLang));

            // Add empty option if requested
            if (includeEmpty) {
                return [
                    { id: '', text: emptyText || this.t('selectCountry') },
                    ...countryList
                ];
            }

            return countryList;
        },

        /**
         * Get country name by English name or ISO code
         *
         * @param countryIdOrCode - English country name or ISO code (e.g., 'Mexico', 'MX', 'United States', 'US')
         * @returns Translated country name or empty string if not found
         */
        getCountryName(countryIdOrCode: string): string {
            // Try as ISO code first
            let countryName = countries.getName(countryIdOrCode, this.currentLang, { select: 'official' });

            if (countryName) {
                return countryName;
            }

            // Try to find by English name
            const allCountries = countries.getNames('en', { select: 'official' });
            const code = Object.entries(allCountries).find(([_, name]) => name === countryIdOrCode)?.[0];

            if (code) {
                return countries.getName(code, this.currentLang, { select: 'official' }) || '';
            }

            return '';
        },

        /**
         * Get the full list of WhatsApp countries (dial codes + localized names)
         * for the current language. Delegates to the self-contained module.
         */
        getWhatsappCountries(): WhatsappCountry[] {
            return getWhatsappCountries(this.currentLang);
        }

    } as I18nStore);
}
